import React, { useEffect, useMemo, useState } from 'react';
import { FiActivity, FiUsers, FiAlertTriangle, FiBarChart2, FiFileText, FiCheckCircle, FiTrendingUp, FiGlobe } from 'react-icons/fi';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const estadoColors = {
  pendiente: '#f59e0b',
  en_curso: '#3b82f6',
  completado: '#10b981',
};

function formatMinutes(minutos) {
  if (!Number.isFinite(minutos) || minutos <= 0) return '0h 0m';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return `${horas}h ${resto}m`;
}

function nombreUsuario(usuario) {
  if (!usuario) return 'Sin asignación';
  if (typeof usuario === 'string') return usuario;
  return `${usuario.nombre || ''} ${usuario.apellidos || ''}`.trim() || usuario.email || 'Trabajador';
}

function calcularMargenPresupuesto(presupuesto = {}) {
  const horasTrabajadores = Array.isArray(presupuesto.horasTrabajadores) ? presupuesto.horasTrabajadores : [];
  const horasTotalesTrabajadas = horasTrabajadores.reduce((sum, item) => sum + (Number(item.horas) || 0), 0);
  const horasPresupuestadas = Number(presupuesto.horasPresupuestadas) || 0;
  const costeHora = Number(presupuesto.costeHora) || 0;
  const tarifaHora = Number(presupuesto.tarifaHora) || 0;

  const ingresosEstimados = horasPresupuestadas * tarifaHora;
  const costeManoObra = horasTotalesTrabajadas * costeHora;
  const beneficioBruto = ingresosEstimados - costeManoObra;
  const margenPct = ingresosEstimados > 0 ? (beneficioBruto / ingresosEstimados) * 100 : 0;

  return {
    horasPresupuestadas,
    horasTotalesTrabajadas,
    costeHora,
    tarifaHora,
    ingresosEstimados,
    costeManoObra,
    beneficioBruto,
    margenPct,
  };
}

export default function MonitorActividad() {
  const { usuario, empresa } = useAuth();
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin';
  const [fichajes, setFichajes] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [trabajadorFiltro, setTrabajadorFiltro] = useState('todos');
  const [landingSummary, setLandingSummary] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!esAdmin) return;

    const cargar = async () => {
      setCargando(true);
      try {
        const [fichajesRes, tareasRes, presupuestosRes, facturasRes] = await Promise.all([
          api.get('/fichajes'),
          api.get('/tareas'),
          api.get('/presupuestos').catch(() => ({ data: [] })),
          api.get('/facturas').catch(() => ({ data: [] })),
        ]);

        setFichajes(Array.isArray(fichajesRes.data) ? fichajesRes.data : []);
        setTareas(Array.isArray(tareasRes.data) ? tareasRes.data : []);
        setPresupuestos(Array.isArray(presupuestosRes.data) ? presupuestosRes.data : []);
        setFacturas(Array.isArray(facturasRes.data) ? facturasRes.data : []);
      } catch (err) {
        console.error('ERROR monitor actividad ->', err);
      } finally {
        setCargando(false);
      }
    };

    cargar();
  }, [esAdmin, empresa?._id]);

  useEffect(() => {
    if (!esAdmin || !empresa?.landingPage?.activo) {
      setLandingSummary(null);
      return;
    }

    let activo = true;
    api.get('/trafico/landing-summary', {
      params: {
        empresaId: empresa?._id,
        slug: empresa?.landingPage?.slug,
      },
    })
      .then(({ data }) => {
        if (activo) setLandingSummary(data || { activo: false, visitasUltimoMes: 0, clientesPotenciales: 0, conversion: 0, leadsRecientes: [] });
      })
      .catch(() => {
        if (activo) setLandingSummary({ activo: false, visitasUltimoMes: 0, clientesPotenciales: 0, conversion: 0, leadsRecientes: [] });
      });

    return () => { activo = false; };
  }, [esAdmin, empresa?._id, empresa?.landingPage?.activo, empresa?.landingPage?.slug]);

  const trabajadores = useMemo(() => {
    const ids = new Map();
    fichajes.forEach((f) => {
      const u = f.usuarioId;
      if (u && u._id) ids.set(String(u._id), u);
    });
    tareas.forEach((t) => {
      const u = t.asignadoA;
      if (u && u._id) ids.set(String(u._id), u);
    });
    return [...ids.values()].map((u) => ({
      id: u._id,
      nombre: nombreUsuario(u),
    }));
  }, [fichajes, tareas]);

  const tareasFiltradas = useMemo(() => {
    return tareas.filter((t) => {
      if (trabajadorFiltro === 'todos') return true;
      return String(t.asignadoA?._id || t.asignadoA) === String(trabajadorFiltro);
    });
  }, [tareas, trabajadorFiltro]);

  const fichajesFiltrados = useMemo(() => {
    return fichajes.filter((f) => {
      if (trabajadorFiltro === 'todos') return true;
      return String(f.usuarioId?._id || f.usuarioId) === String(trabajadorFiltro);
    });
  }, [fichajes, trabajadorFiltro]);

  const documentosFirmados = useMemo(() => {
    const pFirmados = presupuestos.filter((p) => p.firmaInfo?.firmado).map((p) => ({ ...p, tipoDoc: 'Presupuesto' }));
    const fFirmados = facturas.filter((f) => f.firmaInfo?.firmado).map((f) => ({ ...f, tipoDoc: 'Factura' }));
    return [...pFirmados, ...fFirmados].sort((a, b) => new Date(b.firmaInfo.firmadoAt || 0) - new Date(a.firmaInfo.firmadoAt || 0));
  }, [presupuestos, facturas]);

  const presupuestosConMargen = useMemo(() => {
    return presupuestos
      .map((presupuesto) => ({
        ...presupuesto,
        ...calcularMargenPresupuesto(presupuesto),
      }))
      .filter((presupuesto) => Number(presupuesto.horasPresupuestadas) > 0 || Number(presupuesto.costeHora) > 0 || Number(presupuesto.tarifaHora) > 0 || (Array.isArray(presupuesto.horasTrabajadores) && presupuesto.horasTrabajadores.some((h) => Number(h.horas) > 0)))
      .sort((a, b) => (b.beneficioBruto || 0) - (a.beneficioBruto || 0));
  }, [presupuestos]);

  const resumen = useMemo(() => {
    const totalTareas = tareasFiltradas.length;
    const enCurso = tareasFiltradas.filter((t) => t.estado === 'en_curso').length;
    const completadas = tareasFiltradas.filter((t) => t.estado === 'completado').length;
    const pendientes = tareasFiltradas.filter((t) => t.estado === 'pendiente').length;

    const minutosTotales = fichajesFiltrados
      .filter((f) => f.tipo === 'salida' && Number.isFinite(f.duracion))
      .reduce((acc, f) => acc + Number(f.duracion || 0), 0);

    const ranking = trabajadores
      .map((trabajador) => {
        const tareasTrabajador = tareas.filter((t) => String(t.asignadoA?._id || t.asignadoA) === String(trabajador.id));
        const completadasTrabajador = tareasTrabajador.filter((t) => t.estado === 'completado').length;
        const enCursoTrabajador = tareasTrabajador.filter((t) => t.estado === 'en_curso').length;
        const fichajesTrabajador = fichajes.filter((f) => String(f.usuarioId?._id || f.usuarioId) === String(trabajador.id));
        const minutosTrabajador = fichajesTrabajador
          .filter((f) => f.tipo === 'salida' && Number.isFinite(f.duracion))
          .reduce((acc, f) => acc + Number(f.duracion || 0), 0);

        const score = completadasTrabajador * 12 + enCursoTrabajador * 3 + Math.max(0, Math.floor(minutosTrabajador / 60));

        return {
          id: trabajador.id,
          nombre: trabajador.nombre,
          tareas: tareasTrabajador.length,
          completadas: completadasTrabajador,
          enCurso: enCursoTrabajador,
          minutos: minutosTrabajador,
          score,
        };
      })
      .sort((a, b) => b.score - a.score);

    const top = ranking[0];

    return {
      totalTareas,
      enCurso,
      completadas,
      pendientes,
      minutosTotales,
      ranking,
      top,
    };
  }, [fichajes, fichajesFiltrados, tareas, tareasFiltradas, trabajadores]);

  if (!esAdmin) {
    return (
      <div className="page">
        <div className="alert-warning"><FiAlertTriangle /> Solo los administradores y superadministradores pueden ver el monitor de actividad.</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Monitor de actividad</h1>
          <p className="page-subtitle">Seguimiento operativo, rendimiento del equipo y tareas en curso</p>
        </div>
      </div>

      {cargando ? (
        <p className="empty">Cargando actividad…</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div className="card stat-card">
              <div className="stat-icon"><FiActivity /></div>
              <div className="stat-label">Tareas en curso</div>
              <div className="stat-value">{resumen.enCurso}</div>
            </div>
            <div className="card stat-card">
              <div className="stat-icon"><FiBarChart2 /></div>
              <div className="stat-label">Completadas</div>
              <div className="stat-value">{resumen.completadas}</div>
            </div>
            <div className="card stat-card">
              <div className="stat-icon"><FiUsers /></div>
              <div className="stat-label">Pendientes</div>
              <div className="stat-value">{resumen.pendientes}</div>
            </div>
            <div className="card stat-card">
              <div className="stat-icon"><FiBarChart2 /></div>
              <div className="stat-label">Horas trabajadas</div>
              <div className="stat-value">{formatMinutes(resumen.minutosTotales)}</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <div className="filtros-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <label style={{ fontWeight: 700, color: '#334155' }}>Trabajador</label>
              <select
                value={trabajadorFiltro}
                onChange={(e) => setTrabajadorFiltro(e.target.value)}
                style={{ minWidth: 220, padding: '10px 12px', borderRadius: 10, border: '1px solid #dbeafe', background: '#fff' }}
              >
                <option value="todos">Todos los trabajadores</option>
                {trabajadores.map((t) => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 20 }}>
            <div className="card">
              <h3 className="permisos-titulo">Órdenes de trabajo en curso</h3>
              {tareasFiltradas.filter((t) => t.estado !== 'completado').length === 0 ? (
                <p className="empty">No hay tareas activas.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Trabajo</th>
                        <th>Asignado</th>
                        <th>Estado</th>
                        <th>Fecha tope</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tareasFiltradas
                        .filter((t) => t.estado !== 'completado')
                        .sort((a, b) => new Date(a.fechaTope || 0) - new Date(b.fechaTope || 0))
                        .map((t) => (
                          <tr key={t._id}>
                            <td data-label="Trabajo">{t.titulo}</td>
                            <td data-label="Asignado">{nombreUsuario(t.asignadoA)}</td>
                            <td data-label="Estado">
                              <span className="badge" style={{ background: `${estadoColors[t.estado] || '#64748b'}20`, color: estadoColors[t.estado] || '#64748b' }}>
                                {t.estado === 'en_curso' ? 'En curso' : t.estado === 'pendiente' ? 'Pendiente' : t.estado}
                              </span>
                            </td>
                            <td data-label="Fecha tope">{t.fechaTope ? new Date(t.fechaTope).toLocaleDateString('es-ES') : '—'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <h3 className="permisos-titulo">Ranking rendimiento</h3>
              {resumen.ranking.length === 0 ? (
                <p className="empty">Sin datos de rendimiento.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Pos.</th>
                        <th>Empleado</th>
                        <th>Score</th>
                        <th>Horas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.ranking.slice(0, 8).map((r, index) => (
                        <tr key={r.id}>
                          <td data-label="Pos.">#{index + 1}</td>
                          <td data-label="Empleado">{r.nombre}</td>
                          <td data-label="Score">{r.score}</td>
                          <td data-label="Horas">{formatMinutes(r.minutos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {resumen.top && (
            <div className="card" style={{ marginTop: 24 }}>
              <h3 className="permisos-titulo">Top rendimiento</h3>
              <p className="empty" style={{ marginTop: 0 }}>
                <strong>{resumen.top.nombre}</strong> lidera el ranking con <strong>{resumen.top.score}</strong> puntos y <strong>{formatMinutes(resumen.top.minutos)}</strong> de trabajo registrado.
              </p>
            </div>
          )}

          <div className="card" style={{ marginTop: 24 }}>
            <h3 className="permisos-titulo">Margen de beneficio por presupuesto</h3>
            {presupuestosConMargen.length === 0 ? (
              <p className="empty" style={{ marginTop: 0 }}>Todavía no hay presupuestos con horas o margen calculado.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Presupuesto</th>
                      <th>Cliente</th>
                      <th>Horas</th>
                      <th>Coste / h</th>
                      <th>Tarifa / h</th>
                      <th>Beneficio</th>
                      <th>Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presupuestosConMargen.map((p) => (
                      <tr key={p._id}>
                        <td data-label="Presupuesto"><strong>{p.numero}</strong></td>
                        <td data-label="Cliente">{p.clienteId?.nombre || 'Cliente'}</td>
                        <td data-label="Horas">{(p.horasPresupuestadas || 0).toFixed(1)} h</td>
                        <td data-label="Coste / h">{Number(p.costeHora || 0).toFixed(2)} €</td>
                        <td data-label="Tarifa / h">{Number(p.tarifaHora || 0).toFixed(2)} €</td>
                        <td data-label="Beneficio" style={{ color: (p.beneficioBruto || 0) >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                          {Number(p.beneficioBruto || 0).toFixed(2)} €
                        </td>
                        <td data-label="Margen" style={{ color: (p.margenPct || 0) >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                          {Number(p.margenPct || 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {empresa?.landingPage?.activo && (
            <div className="card" style={{ marginTop: 24 }}>
              <h3 className="permisos-titulo" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiGlobe style={{ color: '#2563eb' }} /> Tráfico Landing
              </h3>

              {landingSummary?.activo === false ? (
                <p className="empty" style={{ marginTop: 0 }}>La landing pública está configurada pero aún no hay tráfico registrado en los últimos 30 días.</p>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 18 }}>
                    <div className="card stat-card">
                      <div className="stat-icon"><FiTrendingUp /></div>
                      <div className="stat-label">Visitas último mes</div>
                      <div className="stat-value">{landingSummary?.visitasUltimoMes ?? 0}</div>
                    </div>
                    <div className="card stat-card">
                      <div className="stat-icon"><FiUsers /></div>
                      <div className="stat-label">Leads registrados</div>
                      <div className="stat-value">{landingSummary?.clientesPotenciales ?? 0}</div>
                    </div>
                    <div className="card stat-card">
                      <div className="stat-icon"><FiBarChart2 /></div>
                      <div className="stat-label">Conversión</div>
                      <div className="stat-value">{landingSummary?.conversion ?? 0}%</div>
                    </div>
                  </div>

                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>Empresa</th>
                          <th>Registro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(landingSummary?.leadsRecientes || []).length === 0 ? (
                          <tr>
                            <td colSpan="4" className="empty">No hay leads captados desde la landing en el último mes.</td>
                          </tr>
                        ) : (
                          landingSummary.leadsRecientes.map((lead) => (
                            <tr key={lead._id}>
                              <td data-label="Nombre">{lead.nombre}</td>
                              <td data-label="Email">{lead.email}</td>
                              <td data-label="Empresa">{lead.empresa || '—'}</td>
                              <td data-label="Registro">{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('es-ES') : '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Registro de Firmas Digitales */}
          <div className="card" style={{ marginTop: 24 }}>
            <h3 className="permisos-titulo" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCheckCircle style={{ color: '#10b981' }} /> Documentos Firmados Digitalmente ({documentosFirmados.length})
            </h3>
            {documentosFirmados.length === 0 ? (
              <p className="empty" style={{ marginTop: 0 }}>No hay documentos firmados digitalmente todavía.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Número</th>
                      <th>Firmado por</th>
                      <th>NIF</th>
                      <th>Fecha de firma</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentosFirmados.map((doc) => (
                      <tr key={doc._id}>
                        <td data-label="Tipo"><span className="badge badge-info">{doc.tipoDoc}</span></td>
                        <td data-label="Número"><strong>{doc.numero}</strong></td>
                        <td data-label="Firmado por">{doc.firmaInfo?.firmadoPorNombre || '—'}</td>
                        <td data-label="NIF">{doc.firmaInfo?.firmadoPorNif || '—'}</td>
                        <td data-label="Fecha">{doc.firmaInfo?.firmadoAt ? new Date(doc.firmaInfo.firmadoAt).toLocaleString('es-ES') : '—'}</td>
                        <td data-label="IP"><span className="sub" style={{ fontSize: '0.85rem' }}>{doc.firmaInfo?.ip || 'Registrada'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
